/**
 * The guest's "your place" link (the one line v3, owner 2026-09-29): a short
 * unguessable code per guest, Guest.linkCode, opened at /w/<code> with no
 * login. The page shows the party's own window and, once it has checked in,
 * how many parties are ahead of it; never a name or a phone, and never
 * another party's (owner: "don't show the other parties' names").
 *
 * The code is the only key, so it has to be unguessable, and it rides in a
 * text, so it has to be short. Eight characters from 56 (base62 without the
 * look-alikes 0 O o 1 l I, so a code read aloud at the stand survives) is
 * 56^8, about 9.7e13 codes; the route also rate-limits misses per address.
 * A code is drawn by rejection sampling, so every character is equally
 * likely (a plain byte % 56 would favour the first 32 characters).
 *
 * Pure and client-safe: the caller brings the random bytes (lib/guestLink.ts
 * draws them from node's crypto). No Date, no Math.random.
 */

/** 56 characters: A-Z a-z 2-9 without I O l o (and 0 1 are not in 2-9) */
export const LINK_CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
export const LINK_CODE_LENGTH = 8;
/** bytes at or above this are rejected: 224 = 4 x 56, the largest multiple under 256 */
const ACCEPT_BELOW = Math.floor(256 / LINK_CODE_ALPHABET.length) * LINK_CODE_ALPHABET.length;

/** random bytes enough for one code with room to reject (224/256 accepted: 32 bytes miss ~1e-10) */
export const LINK_CODE_BYTES = 32;

/**
 * A code from random bytes, or null when the bytes ran out before 8
 * characters were accepted (the caller draws again).
 */
export function linkCodeFromBytes(bytes: ArrayLike<number>): string | null {
  let out = '';
  for (let i = 0; i < bytes.length && out.length < LINK_CODE_LENGTH; i++) {
    const b = bytes[i];
    if (b >= ACCEPT_BELOW) continue;
    out += LINK_CODE_ALPHABET[b % LINK_CODE_ALPHABET.length];
  }
  return out.length === LINK_CODE_LENGTH ? out : null;
}

const SHAPE = new RegExp(`^[${LINK_CODE_ALPHABET}]{${LINK_CODE_LENGTH}}$`);

/** could this string be a link code? (the route skips the database for anything else) */
export function isLinkCodeShape(s: unknown): s is string {
  return typeof s === 'string' && SHAPE.test(s);
}

/** the origin every guest link is sent from (tableReadyMessage uses the same one) */
export const PLACE_LINK_ORIGIN = 'https://app.heyality.com';

/** the page a code opens: https://app.heyality.com/w/<code> */
export function placeLinkUrl(code: string, origin: string = PLACE_LINK_ORIGIN): string {
  return `${origin.replace(/\/+$/, '')}/w/${code}`;
}

/** the app-relative path, for a button on our own pages */
export function placeLinkPath(code: string): string {
  return `/w/${code}`;
}

/**
 * A "your place" link as it sits in message TEXT (fixer 5): "/w/" and every
 * letter or digit after it, up to the 8 a code has (a code cut short by a 140-character
 * slice or a clipped tapback quote must not leak its first characters). Wider than the alphabet on purpose (any letter or
 * digit, either case of the "w"), so a code is masked even beside a look-alike,
 * glued to the next word, or quoted back in a tapback
 * (`Liked "...check in: https://app.heyality.com/w/<code>"`). server.js masks
 * its WebSocket sends with this same pattern, written out there
 * (placeLinkMask.test.ts pins the two together).
 */
export const PLACE_LINK_IN_TEXT = /\/[wW]\/[A-Za-z0-9]{1,8}/g;
export const PLACE_LINK_MASKED = '/w/********';

/**
 * Message text as a screen may show it (fixer 5): every /w/<code> becomes
 * /w/********. The code is the only key to that party's page ("I'm here",
 * "Leave the list"), and it is texted in every welcome, so every API and
 * broadcast that hands message text to a client masks it; the SMS the guest
 * receives keeps the real link (it is sent before any of this, from the
 * builder's own text). Everything else in the text is untouched.
 */
export function maskPlaceLinks<T extends string | null | undefined>(text: T): T {
  if (typeof text !== 'string' || text.indexOf('/') < 0) return text;
  return text.replace(PLACE_LINK_IN_TEXT, PLACE_LINK_MASKED) as T;
}
